import { useRef, useState, useEffect, useCallback } from "react"
import styled from "styled-components"
import TaskBar from "./task-bar"
import CursorOverlay from "./CursorOverlay"
import { ChevronLeft, ChevronRight, Maximize2, CalendarDays } from "lucide-react"
import { useSocket } from "../../context/SocketContext"

// ── 스타일 정의 ───────────────────────────────────────────────
const DependencyLine = styled.svg`
  position: absolute; top: 0; left: 0;
  width: 100%; height: 100%;
  pointer-events: none; z-index: 5;
`
const DependencyPath = styled.path`
  fill: none; stroke: #94a3b8;
  stroke-width: 1.5; stroke-dasharray: 4;
  marker-end: url(#arrowhead);
`

const MilestoneContainer = styled.div`
  position: absolute; top: 0; bottom: 0;
  width: 2.5px; background-color: ${p => p.color || "#F43F5E"};
  z-index: 25;
`
const MilestoneMarker = styled.div`
  position: absolute; top: 0px; left: 50%;
  transform: translate(-50%, -50%);
  width: 20px; height: 20px;
  background-color: ${p => p.color || "#F43F5E"};
  border-radius: 50%; display: flex;
  align-items: center; justify-content: center;
  color: white; font-weight: bold; font-size: 15px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  cursor: pointer;
  &:hover::after {
    content: "${p => p.name}";
    position: absolute; top: -30px; left: 50%;
    transform: translateX(-50%);
    background-color: #1f2937; color: white;
    padding: 4px 8px; border-radius: 4px;
    font-size: 12px; white-space: nowrap;
    z-index: 30;
  }
`

const Container = styled.div`
  flex: 1; 
  position: relative;
  background-color: white;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;
`

const NavigationBar = styled.div`
  position: sticky;
  top: 0;
  z-index: 30;
  padding: 10px 14px;
  border-bottom: 1px solid #e5e7eb;
  background-color: white;
  display: flex;
  align-items: center;
  gap: 10px;
`

const NavButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: none;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  transition: background 0.12s, color 0.12s;
  border-radius: 6px;

  &:hover {
    background: #f1f5f9;
    color: #0f172a;
  }
`

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 32px;
  padding: 0 12px;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
  letter-spacing: -0.01em;

  svg { color: #64748b; transition: color 0.12s; }

  &:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
    color: #0f172a;
    svg { color: #2563eb; }
  }
`

const RefDateControl = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 10px;
  font-size: 12.5px;
  color: #64748b;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;

  svg { color: #94a3b8; }

  .label { font-weight: 600; }

  input[type="date"] {
    border: none;
    outline: none;
    font-size: 12.5px;
    color: #0f172a;
    background: transparent;
    cursor: pointer;
    padding: 0;
    font-family: inherit;
  }
`

const TodayButton = styled(ActionButton)`
  color: #1d4ed8;
  background: #eff6ff;
  border-color: #bfdbfe;

  svg { color: #2563eb; }

  &:hover {
    background: #dbeafe;
    border-color: #93c5fd;
    color: #1e40af;
  }
`

const TimelineContainer = styled.div`
  display: flex; flex-direction: column;
  border-bottom: 1px solid #e5e7eb;
`

const TimelineScale = styled.div`
  display: flex;
`

const TimelineItem = styled.div`
  display: flex; align-items: center; justify-content: center;
  text-align: center; border-right: 1px solid #e5e7eb;
  font-size: 12px; color: #6b7280;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  background-color: ${p => p.bg || "transparent"};
  font-weight: ${p => (p.bold ? "bold" : "normal")};
  flex-shrink: 0;
`

const ChartContent = styled.div`
  position: relative;
`

const GridLines = styled.div`
  position: absolute; top: 0; left: 0; right: 0; bottom: 0;
`

const GridLine = styled.div`
  position: absolute; top: 0; bottom: 0;
  border-right: 1px solid ${p =>
    p.isMonth ? "#d1d5db" : p.isWeek ? "#e5e7eb" : "#f3f4f6"};
`

const TasksContainer = styled.div`
  display: grid; gap: 4px; padding-top: 16px;
`

const TaskRow = styled.div`
  position: relative; height: 48px; padding: 0 16px;
  cursor: pointer;
`

const ScrollableArea = styled.div`
  flex: 1;
  overflow-x: auto;
  overflow-y: auto;
  background-color: #f8f9fa;
`

const ScrollableContent = styled.div`
  min-width: 100%;
  display: inline-block;
`

// ── 컴포넌트 본문 ─────────────────────────────────────────────
export default function GanttChart({
  activeTab,
  setActiveTab,
  tasks,
  users,
  milestones,
  selectedTask,
  setSelectedTask,
  updateTask,
  zoomLevel,
  viewMode,
  setSelectedMilestone,
  selectedMilestone,
  setZoomLevel,
  referenceDate,
  setReferenceDate,
}) {
  const containerRef = useRef(null)
  const [timeline, setTimeline] = useState([])
  const [dragging, setDragging] = useState(null)
  const [startDate, setStartDate] = useState(new Date(2025, 2, 1))
  const [endDate, setEndDate] = useState(new Date(2025, 3, 30))
  const containerWidthRef = useRef(1200)
  const [containerWidth, setContainerWidth] = useState(1200)
  const [containerHeight, setContainerHeight] = useState(600)

  // 컨테이너 너비/높이 추적 (ref + state)
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      const w = el.clientWidth;
      if (w > 0 && w !== containerWidthRef.current) {
        containerWidthRef.current = w;
        setContainerWidth(w);
      }
      const h = el.clientHeight;
      if (h > 0) setContainerHeight(prev => (prev === h ? prev : h));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [])
  const { emitCursorMove, currentChartId } = useSocket()

  // 차트 위 마우스 움직임 → 소켓 전송
  // 좌표는 ChartContent(콘텐츠 캔버스) 요소 기준으로 직접 측정 —
  // 스크롤 위치/날짜 헤더 높이가 양쪽에서 달라도 오프셋이 생기지 않음
  // x는 줌/표시범위가 달라도 동일하게 보이도록 날짜(UTC 일수, 소수)로 정규화해 전송
  const chartContentRef = useRef(null);
  const dayNumOf = (d) => Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / 86400000;
  const handleCursorMove = useCallback((e) => {
    if (!currentChartId || !chartContentRef.current) return;
    const rect = chartContentRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const day = dayNumOf(startDate) + x / (40 * zoomLevel);
    // fx/fy: 수신 측이 다른 뷰라 정밀 매핑이 불가능할 때 쓰는 화면 비율 폴백
    emitCursorMove({
      day, y, view: 'classic',
      fx: e.clientX / window.innerWidth,
      fy: e.clientY / window.innerHeight,
    });
  }, [currentChartId, emitCursorMove, startDate, zoomLevel]); // eslint-disable-line react-hooks/exhaustive-deps
  
// 스타일 추가
const DateRangeDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 16px;
  padding: 6px 12px;
  background-color: #f9fafb;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
`

const DateText = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #374151;
`

// 스타일 추가
const DateControlGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const DateControl = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`

const DateRangeLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  letter-spacing: -0.01em;
`

const DateSegment = styled.div`
  display: inline-flex;
  align-items: center;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  transition: border-color 0.12s;

  &:hover { border-color: #cbd5e1; }
`

const DateLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
  padding: 0 12px;
  min-width: 86px;
  text-align: center;
  letter-spacing: -0.01em;
  border-left: 1px solid #e2e8f0;
  border-right: 1px solid #e2e8f0;
  line-height: 30px;
`

// 시작일 조정 함수
const navigateStartDate = (dir) => {
  let diff;
  
  if (viewMode === "month") {
    diff = dir === "prev" ? -1 : 1; // 1개월씩
    setStartDate(d => {
      const newDate = new Date(d);
      newDate.setMonth(d.getMonth() + diff);
      return newDate;
    });
  } else if (viewMode === "week") {
    diff = dir === "prev" ? -7 : 7; // 1주씩
    setStartDate(d => new Date(d.getTime() + diff * 24 * 60 * 60 * 1000));
  } else {
    diff = dir === "prev" ? -7 : 7; // 7일씩
    setStartDate(d => new Date(d.getTime() + diff * 24 * 60 * 60 * 1000));
  }
};

// 종료일 조정 함수
const navigateEndDate = (dir) => {
  let diff;
  
  if (viewMode === "month") {
    diff = dir === "prev" ? -1 : 1; // 1개월씩
    setEndDate(d => {
      const newDate = new Date(d);
      newDate.setMonth(d.getMonth() + diff);
      return newDate;
    });
  } else if (viewMode === "week") {
    diff = dir === "prev" ? -7 : 7; // 1주씩
    setEndDate(d => new Date(d.getTime() + diff * 24 * 60 * 60 * 1000));
  } else {
    diff = dir === "prev" ? -7 : 7; // 7일씩
    setEndDate(d => new Date(d.getTime() + diff * 24 * 60 * 60 * 1000));
  }
};
  // ── 날짜 유틸리티 ──
  // UTC 자정 기준 날짜 생성 (시간대 오차 제거)
  const toUTCDate = (d) => {
    const date = new Date(d);
    return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
  };

  // 두 날짜 사이의 일수 차이 (UTC 기준, 정확)
  const daysBetween = (a, b) => {
    return Math.round((toUTCDate(b) - toUTCDate(a)) / (1000 * 60 * 60 * 24));
  };

  // 해당 월의 일수
  const daysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();

  // 하루 너비
  const getDayWidth = () => 40 * zoomLevel;

  // 월별 보기에서 특정 월의 너비 (실제 일수 반영)
  const getMonthWidth = (year, month) => getDayWidth() * daysInMonth(year, month);

  const getUnitWidth = () => {
    const baseWidth = 40 * zoomLevel;
    if (viewMode === "week") return baseWidth * 7;
    return baseWidth; // 일별: 1일 너비 (월별은 getMonthWidth 사용)
  };

  // 타임라인 범위 설정 - 화면을 채울 만큼 확장
  useEffect(() => {
    const cw = containerWidthRef.current || 1200;
    const dayWidth = 40 * zoomLevel;

    const today = new Date();
    let minD, maxD;
    if (tasks?.length) {
      const allDates = [...tasks.flatMap(t => [t.startDate, t.endDate]), today];
      minD = new Date(Math.min(...allDates.map(d => d.getTime())));
      maxD = new Date(Math.max(...allDates.map(d => d.getTime())));
      if (milestones?.length) {
        const ms = milestones.map(m => m.date);
        minD = new Date(Math.min(minD.getTime(), ...ms.map(d => d.getTime())));
        maxD = new Date(Math.max(maxD.getTime(), ...ms.map(d => d.getTime())));
      }
      minD.setDate(minD.getDate() - 3);
      maxD.setDate(maxD.getDate() + 3);
    } else {
      minD = new Date(today);
      minD.setDate(minD.getDate() - 7);
      maxD = new Date(today);
      maxD.setDate(maxD.getDate() + 7);
    }

    // 스크롤 끝에서도 빈 공간 없도록 화면 한 폭 이상 추가
    const screenDays = Math.ceil(cw / dayWidth) + 7;
    maxD.setDate(maxD.getDate() + screenDays);

    setStartDate(minD);
    setEndDate(maxD);
  }, [tasks, milestones, viewMode, zoomLevel])

  useEffect(() => {
  const items = [];
  
  if (viewMode === "week") {
    // 주별 보기: 시작일이 포함된 주의 월요일부터 시작
    const current = new Date(startDate);
    
    // 시작일이 포함된 주의 월요일 찾기
    const dayOfWeek = current.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    current.setDate(current.getDate() + mondayOffset);
    
    // 종료일이 포함된 주의 일요일까지 확장
    const extendedEndDate = new Date(endDate);
    const endDayOfWeek = extendedEndDate.getDay();
    const sundayOffset = endDayOfWeek === 0 ? 0 : 7 - endDayOfWeek;
    extendedEndDate.setDate(extendedEndDate.getDate() + sundayOffset);
    
    // 주별 항목 생성
    while (current <= extendedEndDate) {
      const weekStart = new Date(current);
      const weekEnd = new Date(current);
      weekEnd.setDate(weekStart.getDate() + 6);
      
      // 주차 계산 (해당 월의 몇 번째 주인지)
      const weekNumber = Math.ceil(weekStart.getDate() / 7);
      
      items.push({
        date: new Date(weekStart),
        label: `${weekStart.getMonth() + 1}월 ${weekNumber}주`,
        isMonth: false,
        isWeek: true,
        isToday: false,
        weekStart: new Date(weekStart),
        weekEnd: new Date(weekEnd)
      });
      
      // 다음 주로 이동
      current.setDate(current.getDate() + 7);
    }
    
  } else if (viewMode === "month") {
    // 월별 보기: 매월 1일만 표시
    const d = new Date(startDate);
    while (d <= endDate) {
      if (d.getDate() === 1) {
        items.push({
          date: new Date(d),
          label: d.toLocaleString("ko-KR", { month: "long", year: "numeric" }),
          isMonth: true,
          isWeek: false,
          isToday: false,
        });
      }
      d.setMonth(d.getMonth() + 1, 1);
    }
    
  } else {
    // 일별 보기: 기존 로직
    const d = new Date(startDate);
    while (d <= endDate) {
      const date = new Date(d);
      items.push({
        date,
        label: `${d.getDate()}`,
        isMonth: d.getDate() === 1,
        isWeek: d.getDay() === 0,
        isToday: d.toDateString() === new Date().toDateString(),
      });
      d.setDate(d.getDate() + 1);
    }
  }
  
  setTimeline(items);
}, [startDate, endDate, viewMode, zoomLevel]);

  // 드래그 핸들러
  const handleMouseDown = (taskId, type) => e => {
    e.preventDefault()
    setDragging({ taskId, type })
  }

  const handleMouseMove = (e) => {
    if (!dragging || !containerRef.current) return

    const rect = containerRef.current.getBoundingClientRect()
    const x = e.clientX - rect.left
    const dayWidth = getDayWidth()

    // 자동 스크롤
    const scrollThreshold = 50
    const scrollSpeed = 20
    
    if (x < scrollThreshold) {
      containerRef.current.scrollLeft -= scrollSpeed
    } else if (x > rect.width - scrollThreshold) {
      containerRef.current.scrollLeft += scrollSpeed
    }

    // 위치 계산
    const actualX = x + containerRef.current.scrollLeft
    const daysDiff = Math.floor(actualX / dayWidth)
    const candidateDate = new Date(startDate.getTime())
    candidateDate.setDate(startDate.getDate() + daysDiff)

    // 태스크 업데이트
    const task = tasks.find(t => t.id === dragging.taskId)
    if (!task) return

    const updated = { ...task }
    
    if (dragging.type === "move") {
      const duration = Math.ceil((task.endDate - task.startDate) / (1000*60*60*24))
      updated.startDate = new Date(candidateDate)
      updated.endDate = new Date(candidateDate.getTime() + (duration * 24 * 60 * 60 * 1000))
      
      if (updated.startDate < startDate) setStartDate(new Date(updated.startDate))
      if (updated.endDate > endDate) setEndDate(new Date(updated.endDate))
      
    } else if (dragging.type === "resize-start") {
      if (candidateDate < updated.endDate) {
        updated.startDate = new Date(candidateDate)
      }
    } else if (dragging.type === "resize-end") {
      if (candidateDate > updated.startDate) {
        updated.endDate = new Date(candidateDate)
      }
    }

    updateTask(updated)
  }

  const handleMouseUp = () => setDragging(null)

  // 네비게이션
  const navigateTimeline = dir => {
    const diff = dir === "prev" ? -30 : 30
    setStartDate(d => new Date(d.setDate(d.getDate() + diff)))
    setEndDate(d => new Date(d.setDate(d.getDate() + diff)))
  }

  // ── 위치 계산 공통 함수 ──
  // 타임라인 시작부터 특정 날짜까지의 픽셀 위치 (모든 viewMode 공통)
  const getPixelForDate = (date) => {
    const dayWidth = getDayWidth();

    if (viewMode === "month") {
      // 월별: 각 월의 실제 일수를 반영하여 누적 계산
      let px = 0;
      const targetUTC = toUTCDate(date);
      const startY = startDate.getFullYear();
      const startM = startDate.getMonth();
      const dateY = date.getFullYear();
      const dateM = date.getMonth();

      // timeline 시작월의 1일 기준
      const timelineStart = new Date(startY, startM, 1);
      const monthsDiff = (dateY - startY) * 12 + (dateM - startM);

      // 시작월부터 대상월 전월까지 각 월의 너비를 합산
      for (let i = 0; i < monthsDiff; i++) {
        const y = startY + Math.floor((startM + i) / 12);
        const m = (startM + i) % 12;
        px += daysInMonth(y, m) * dayWidth;
      }

      // 대상월 내 일수 비율
      const dayOfMonth = date.getDate() - 1; // 0-indexed
      px += dayOfMonth * dayWidth;

      return px;

    } else if (viewMode === "week") {
      // 주별: 타임라인 첫 번째 월요일 기준으로 일수 차이 계산
      const firstMonday = timeline.length > 0 ? timeline[0].date : startDate;
      const days = daysBetween(firstMonday, date);
      return days * dayWidth;

    } else {
      // 일별: UTC 기준 일수 차이
      const days = daysBetween(startDate, date);
      return days * dayWidth;
    }
  };

  // 위치 & 너비 계산
  const getTaskPosition = t => {
    const left = getPixelForDate(t.startDate);
    const right = getPixelForDate(t.endDate);
    const minWidth = getDayWidth();
    return { left, width: Math.max(right - left, minWidth) };
  };

  const getTodayPosition = (today) => {
    return getPixelForDate(today);
  };

  const getMilestonePosition = m => {
    return { left: getPixelForDate(m.date) };
  };

  // 전체 타임라인 너비 계산
  const getTotalWidth = () => {
    if (viewMode === "month") {
      let total = 0;
      for (const item of timeline) {
        total += getMonthWidth(item.date.getFullYear(), item.date.getMonth());
      }
      return total;
    }
    return timeline.length * getUnitWidth();
  };

  // 타임라인 아이템의 너비 (월별은 실제 일수 반영)
  const getItemWidth = (item) => {
    if (viewMode === "month") {
      return getMonthWidth(item.date.getFullYear(), item.date.getMonth());
    }
    return getUnitWidth();
  };

  // 타임라인 아이템의 왼쪽 위치 (누적)
  const getItemLeft = (index) => {
    let left = 0;
    for (let i = 0; i < index; i++) {
      left += getItemWidth(timeline[i]);
    }
    return left;
  };

  const totalWidth = Math.max(getTotalWidth(), containerWidth);

  return (
    <Container
      onMouseMove={dragging ? handleMouseMove : undefined}
      onMouseUp={dragging ? handleMouseUp : undefined}
      onMouseLeave={dragging ? handleMouseUp : undefined}
    >
      {/* 네비게이션 고정 */}
    <NavigationBar>
      <DateControlGroup>
        <DateControl>
          <DateRangeLabel>시작</DateRangeLabel>
          <DateSegment>
            <NavButton onClick={() => navigateStartDate("prev")} title="시작일 이전">
              <ChevronLeft size={16} />
            </NavButton>
            <DateLabel>
              {startDate.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' })}
            </DateLabel>
            <NavButton onClick={() => navigateStartDate("next")} title="시작일 다음">
              <ChevronRight size={16} />
            </NavButton>
          </DateSegment>
        </DateControl>

        <DateControl>
          <DateRangeLabel>종료</DateRangeLabel>
          <DateSegment>
            <NavButton onClick={() => navigateEndDate("prev")} title="종료일 이전">
              <ChevronLeft size={16} />
            </NavButton>
            <DateLabel>
              {endDate.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' })}
            </DateLabel>
            <NavButton onClick={() => navigateEndDate("next")} title="종료일 다음">
              <ChevronRight size={16} />
            </NavButton>
          </DateSegment>
        </DateControl>
      </DateControlGroup>

      <ActionButton
        onClick={() => {
          if (!tasks?.length) return;
          const allDates = tasks.flatMap(t => [t.startDate, t.endDate]);
          let minD = new Date(Math.min(...allDates.map(d => d.getTime())));
          let maxD = new Date(Math.max(...allDates.map(d => d.getTime())));
          if (milestones?.length) {
            const ms = milestones.map(m => m.date);
            minD = new Date(Math.min(minD.getTime(), ...ms.map(d => d.getTime())));
            maxD = new Date(Math.max(maxD.getTime(), ...ms.map(d => d.getTime())));
          }

          if (viewMode === "day") {
            minD.setDate(minD.getDate() - 3);
            maxD.setDate(maxD.getDate() + 3);
          } else if (viewMode === "week") {
            minD.setDate(minD.getDate() - 7);
            maxD.setDate(maxD.getDate() + 7);
          } else {
            minD.setMonth(minD.getMonth() - 1);
            maxD.setMonth(maxD.getMonth() + 1);
          }

          setStartDate(minD);
          setEndDate(maxD);

          if (containerRef.current && setZoomLevel) {
            const containerWidth = containerRef.current.clientWidth;
            const totalDays = daysBetween(minD, maxD);
            let units, baseWidth;
            if (viewMode === "month") {
              units = Math.ceil(totalDays / 30);
              baseWidth = 40 * 30;
            } else if (viewMode === "week") {
              units = Math.ceil(totalDays / 7);
              baseWidth = 40 * 7;
            } else {
              units = totalDays;
              baseWidth = 40;
            }
            const idealZoom = containerWidth / (units * baseWidth);
            const clampedZoom = Math.max(0.1, Math.min(3, Math.round(idealZoom * 10) / 10));
            setZoomLevel(clampedZoom);
          }
        }}
        title="태스크 기간에 맞춰 줌"
      >
        <Maximize2 size={14} /> 전체 보기
      </ActionButton>

      {setReferenceDate && (
        <RefDateControl style={{ marginLeft: 'auto' }} title="목표 진척률 계산 기준일">
          <CalendarDays size={13} />
          <span className="label">기준일</span>
          <input
            type="date"
            value={(() => {
              const d = referenceDate ? new Date(referenceDate) : new Date()
              const y = d.getFullYear()
              const m = String(d.getMonth() + 1).padStart(2, '0')
              const day = String(d.getDate()).padStart(2, '0')
              return `${y}-${m}-${day}`
            })()}
            onChange={(e) => {
              const v = e.target.value
              if (!v) return
              const [y, m, d] = v.split('-').map(Number)
              setReferenceDate(new Date(y, m - 1, d))
            }}
          />
        </RefDateControl>
      )}

      <TodayButton
        onClick={() => {
          const today = new Date();
          if (viewMode === "month") {
            setStartDate(new Date(today.getFullYear(), today.getMonth() - 2, 1));
            setEndDate(new Date(today.getFullYear(), today.getMonth() + 3, 0));
          } else if (viewMode === "week") {
            setStartDate(new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000));
            setEndDate(new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000));
          } else {
            setStartDate(new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000));
            setEndDate(new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000));
          }
        }}
        title="오늘 기준으로 이동"
        style={setReferenceDate ? {} : { marginLeft: 'auto' }}
      >
        <CalendarDays size={14} /> 오늘
      </TodayButton>
    </NavigationBar>

      {/* 스크롤 가능한 영역 */}
      <ScrollableArea ref={containerRef} onMouseMove={handleCursorMove} data-cursor-zone="timeline">
        <ScrollableContent style={{ width: totalWidth }}>
          {/* 타임라인 헤더 */}

{(() => {
  // ── 타임라인 헤더 데이터 계산 (3행 공통 기반) ──
  // 각 타임라인 아이템의 너비 배열
  const colWidths = timeline.map(item =>
    viewMode === "month" ? getItemWidth(item) : getUnitWidth()
  );
  const itemsTotal = colWidths.reduce((s, w) => s + w, 0);

  // CSS grid-template-columns 생성
  const gtc = colWidths.map(w => `${w}px`).join(' ') +
    (itemsTotal < totalWidth ? ` ${totalWidth - itemsTotal}px` : '');
  const colCount = colWidths.length + (itemsTotal < totalWidth ? 1 : 0);

  // 활성 범위 판별
  const activeYears = new Set();
  const activeMonthKeys = new Set();
  let activeEndDate = null;
  if (tasks?.length) {
    const allDates = tasks.flatMap(t => [t.startDate, t.endDate]);
    const minTaskD = new Date(Math.min(...allDates.map(d => d.getTime())));
    const maxTaskD = new Date(Math.max(...allDates.map(d => d.getTime())));
    activeEndDate = maxTaskD;
    for (let y = minTaskD.getFullYear(); y <= maxTaskD.getFullYear(); y++) activeYears.add(y);
    let md = new Date(minTaskD.getFullYear(), minTaskD.getMonth(), 1);
    while (md <= maxTaskD) {
      activeMonthKeys.add(`${md.getFullYear()}-${md.getMonth()}`);
      md.setMonth(md.getMonth() + 1);
    }
  }

  // ── 1행: 연도 그룹 (span 계산) ──
  const yearGroups = [];
  let yi = 0;
  while (yi < timeline.length) {
    const year = timeline[yi].date.getFullYear();
    const start = yi;
    while (yi < timeline.length && timeline[yi].date.getFullYear() === year) yi++;
    yearGroups.push({ year, colStart: start + 1, colEnd: yi + 1 });
  }
  // 마지막 그룹이 패딩 열까지 포함
  if (itemsTotal < totalWidth && yearGroups.length > 0) {
    yearGroups[yearGroups.length - 1].colEnd = colCount + 1;
  }

  // ── 2행: 월 그룹 (span 계산) ──
  const monthGroups = [];
  let mi = 0;
  while (mi < timeline.length) {
    const year = timeline[mi].date.getFullYear();
    const month = timeline[mi].date.getMonth();
    const start = mi;
    while (mi < timeline.length && timeline[mi].date.getFullYear() === year && timeline[mi].date.getMonth() === month) mi++;
    monthGroups.push({ year, month, colStart: start + 1, colEnd: mi + 1 });
  }
  if (itemsTotal < totalWidth && monthGroups.length > 0) {
    monthGroups[monthGroups.length - 1].colEnd = colCount + 1;
  }

  return (
    <TimelineContainer style={{ width: totalWidth }}>
      {/* 1행: 연도 */}
      <div style={{ display: 'grid', gridTemplateColumns: gtc }}>
        {yearGroups.map((yg, i) => {
          const isActive = activeYears.has(yg.year);
          return (
            <div key={`y-${i}`} style={{
              gridColumn: `${yg.colStart} / ${yg.colEnd}`,
              height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
              backgroundColor: isActive ? '#1e3a5f' : '#94a3b8',
              color: '#fff', fontSize: '13px', fontWeight: 'bold',
              borderRight: '2px solid #152d4a',
            }}>
              {yg.year}년
            </div>
          );
        })}
      </div>

      {/* 2행: 월 */}
      <div style={{ display: 'grid', gridTemplateColumns: gtc }}>
        {monthGroups.map((mg, i) => {
          const isActive = activeMonthKeys.has(`${mg.year}-${mg.month}`);
          return (
            <div key={`m-${i}`} style={{
              gridColumn: `${mg.colStart} / ${mg.colEnd}`,
              height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center',
              backgroundColor: isActive ? '#f0f4f8' : '#e8eaed',
              color: isActive ? '#374151' : '#9ca3af',
              fontSize: '12px', borderRight: '1px solid #d1d5db',
            }}>
              {new Date(mg.year, mg.month).toLocaleString("ko-KR", { month: "long" })}
            </div>
          );
        })}
      </div>

      {/* 3행: 상세 (일자/주차) */}
      {viewMode === "day" && (() => {
        const dayW = getUnitWidth();

        if (dayW >= 25) {
          return (
            <div style={{ display: 'grid', gridTemplateColumns: gtc }}>
              {timeline.map((item, i) => (
                <div key={`d-${i}`} style={{
                  height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: item.isToday ? '#3B82F6' : '#6b7280',
                  backgroundColor: item.isToday ? '#dbeafe' : (activeEndDate && item.date > activeEndDate ? '#f8f9fa' : 'transparent'),
                  borderRight: '1px solid #f3f4f6', fontSize: '11px',
                  fontWeight: item.isToday ? 'bold' : 'normal',
                }}>
                  {item.label}
                </div>
              ))}
              {itemsTotal < totalWidth && <div style={{ height: 26, backgroundColor: '#f8f9fa' }} />}
            </div>
          );
        }

        if (dayW >= 4) {
          // 주차 묶기
          const weekGroups = [];
          let ws = 0;
          for (let i = 1; i <= timeline.length; i++) {
            const isEnd = i === timeline.length;
            const isMonday = !isEnd && timeline[i].date.getDay() === 1;
            if (isMonday || isEnd) {
              const d = timeline[ws].date;
              const w = (i - ws) * dayW;
              weekGroups.push({
                label: `${d.getMonth() + 1}/${d.getDate()}`,
                colStart: ws + 1, colEnd: i + 1,
                isToday: timeline.slice(ws, i).some(t => t.isToday),
                width: w,
              });
              ws = i;
            }
          }
          if (itemsTotal < totalWidth && weekGroups.length > 0) {
            weekGroups[weekGroups.length - 1].colEnd = colCount + 1;
          }
          return (
            <div style={{ display: 'grid', gridTemplateColumns: gtc }}>
              {weekGroups.map((wg, i) => (
                <div key={`wk-${i}`} style={{
                  gridColumn: `${wg.colStart} / ${wg.colEnd}`,
                  height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: wg.isToday ? '#3B82F6' : '#6b7280',
                  backgroundColor: wg.isToday ? '#dbeafe' : '#fafafa',
                  borderRight: '1px solid #e5e7eb', fontSize: '10px',
                }}>
                  {wg.width >= 30 ? wg.label : ''}
                </div>
              ))}
            </div>
          );
        }

        return itemsTotal < totalWidth ? (
          <div style={{ height: 20, backgroundColor: '#f0f1f3' }} />
        ) : null;
      })()}

      {viewMode === "week" && (
        <div style={{ display: 'grid', gridTemplateColumns: gtc }}>
          {timeline.map((item, i) => (
            <div key={`wk-${i}`} style={{
              height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#6b7280', backgroundColor: '#fafafa',
              borderRight: '1px solid #e5e7eb', fontSize: '11px',
            }}>
              {item.label}
            </div>
          ))}
          {itemsTotal < totalWidth && <div style={{ height: 26, backgroundColor: '#f0f1f3' }} />}
        </div>
      )}
    </TimelineContainer>
  );
})()}

          {/* 차트 콘텐츠 */}
          {/* minHeight: 작업이 적어도 캔버스가 보이는 영역을 꽉 채워
              빈 공간에서도 원격 커서가 실제 위치에 표시되도록 함 */}
          <ChartContent ref={chartContentRef} style={{ width: totalWidth, minHeight: containerHeight }}>
            {/* 그리드 */}
          <GridLines>
            {(() => {
              // 활성 영역 판별
              let activeEnd = null;
              if (tasks?.length) {
                const maxTaskDate = new Date(Math.max(...tasks.map(t => t.endDate.getTime())));
                activeEnd = maxTaskDate;
              }
              const gridItems = timeline.map((item, i) => {
                const isInactive = activeEnd && item.date > activeEnd;
                return (
                  <GridLine
                    key={i}
                    isMonth={viewMode === "month"}
                    isWeek={viewMode === "week"}
                    style={{
                      left: getItemLeft(i),
                      width: getItemWidth(item),
                      backgroundColor: isInactive ? "#f8f9fa" : "transparent",
                      borderRight: viewMode === "month" ? "2px solid #d1d5db" :
                                  viewMode === "week" ? "1px solid #e5e7eb" : "1px solid #f3f4f6"
                    }}
                  />
                );
              });
              // 그리드도 totalWidth까지 채우기
              const gridEnd = timeline.length > 0 ? getItemLeft(timeline.length - 1) + getItemWidth(timeline[timeline.length - 1]) : 0;
              if (gridEnd < totalWidth) {
                gridItems.push(
                  <GridLine key="pad" style={{ left: gridEnd, width: totalWidth - gridEnd, backgroundColor: "#f8f9fa" }} />
                );
              }
              return gridItems;
            })()}
          </GridLines>
 {/* 오늘 날짜 표시선 추가 */}
  {(() => {
    const today = new Date();
    const todayPosition = getTodayPosition(today);
    
    if (todayPosition !== null) {
      return (
        <div
          style={{
            position: 'absolute',
            left: todayPosition,
            top: 0,
            bottom: 0,
            width: '2px',
            backgroundColor: '#ef4444',
            zIndex: 30,
            pointerEvents: 'none'
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: '-5px',
              left: '-6px',
              width: '14px',
              height: '14px',
              backgroundColor: '#ef4444',
              borderRadius: '50%'
            }}
          />
        </div>
      );
    }
    return null;
  })()}
            {/* 마일스톤 */}
            {milestones.map(m => {
              const { left } = getMilestonePosition(m)
              if (left < 0 || left > totalWidth) return null
              return (
                <MilestoneContainer key={m.id} color={m.color} style={{ left }}>
                  <MilestoneMarker color={m.color} name={m.name} onClick={(e) => {
            e.stopPropagation() // 이벤트 버블링 방지
            setSelectedTask(null) // 태스크 선택 해제
            setSelectedMilestone(m.id) // 마일스톤 선택
            setActiveTab("milestones") // 마일스톤 상세 탭 활성화
          }}>★</MilestoneMarker>
                </MilestoneContainer>
              )
            })}

            {/* 의존선 */}
            <DependencyLine>
              <defs>
                <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                  <polygon points="0 0, 10 3.5, 0 7" fill="#94a3b8" />
                </marker>
              </defs>
              {tasks.flatMap(task =>
                (task.dependencies || []).map(depId => {
                  const from = tasks.find(t => t.id === depId)
                  if (!from) return null
                  const { left: fx, width: fw } = getTaskPosition(from)
                  const { left: tx } = getTaskPosition(task)
                  const fy = tasks.findIndex(t => t.id === from.id) * 52 + 40
                  const ty = tasks.findIndex(t => t.id === task.id) * 52 + 40
                  const cx = (fx + fw + tx) / 2
                  return (
                    <DependencyPath
                      key={`${depId}-${task.id}`}
                      d={`M ${fx + fw} ${fy} C ${cx} ${fy}, ${cx} ${ty}, ${tx} ${ty}`}
                    />
                  )
                })
              )}
            </DependencyLine>

            {/* 작업 바 */}
            <TasksContainer>
              {tasks.map(task => {
                const { left, width } = getTaskPosition(task)
                const user = users.find(u => u.id === task.assignee)
                return (
                  <TaskRow key={task.id} onClick={() => {setSelectedTask(task.id)
                    setActiveTab("tasks")}
                  }>
                    <TaskBar
                      task={task}
                      user={user}
                      left={left}
                      width={width}
                      isSelected={selectedTask === task.id}
                      onMoveStart={handleMouseDown(task.id, "move")}
                      onResizeStart={handleMouseDown(task.id, "resize-start")}
                      onResizeEnd={handleMouseDown(task.id, "resize-end")}
                    />
                  </TaskRow>
                )
              })}
            </TasksContainer>

            {/* 원격 커서 오버레이 */}
            <CursorOverlay
              view="classic"
              dayToX={(day) => (day - dayNumOf(startDate)) * 40 * zoomLevel}
              scrollRef={containerRef}
            />
          </ChartContent>
        </ScrollableContent>
      </ScrollableArea>
    </Container>
  )
}